import React, { Suspense } from 'react';
import { Button } from '~/primitives/button';
import type { NavigateOptions } from '@tanstack/react-router';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import { getAvatarUrl } from '~/util';
import { SiDiscord } from '@icons-pack/react-simple-icons';
import Navbar from './Navbar';
import ThemedImage from '~/partials/ThemedImage';
import { useTheme } from './Providers/ThemeContext';
import { adjustToClosest } from './Providers/theme';
import chroma from 'chroma-js';
import { Bell } from 'lucide-react';
import Notifications from './Notifications';
import { login } from '~/auth/utils';
import { Link, useLoaderData, useLocation } from '@tanstack/react-router';
import { useAuthData, useScopes } from './Providers/AuthProvider';
import Load from './Load';
import { Switch } from '~/primitives/switch';
import { useSettingsStore } from '~/util/stores/settings';
import { Separator } from '~/primitives/separator';

interface DropdownProfileItemProps {
    label: string;
    to?: NavigateOptions;
    onClick?: () => void;
}

const DropdownProfileItem: React.FC<DropdownProfileItemProps> = ({
    label,
    to,
    onClick,
}) => {
    return (
        <DropdownMenuItem
            asChild
            onClick={() => {
                onClick?.();
            }}
        >
            <Link preload="intent" preloadDelay={100} {...to}>
                {label}
            </Link>
        </DropdownMenuItem>
    );
};

const Header: React.FC = () => {
    const auth = useAuthData();
    const { scopes } = useScopes();
    const avatarUrl = getAvatarUrl(auth);
    const { primaryColor, isDefaultColor } = useTheme();

    const notificationCount = useLoaderData({
        from: '__root__',
        select: ({ notifications }) => notifications.length,
    });

    const { staff_mode, setStaffMode } = useSettingsStore();

    const hue = chroma(primaryColor).get('hsl.h');
    const lightness = chroma(primaryColor).get('hsl.l') * 100 * 2;
    const saturation = chroma(primaryColor).get('hsl.s') * 100;
    const highlightHue = adjustToClosest(hue, 23);
    const highlightLightness = !isNaN(hue)
        ? 40 * (1 - Math.abs((hue % 120) - 60) / 60)
        : 0;

    const pathname = useLocation({ select: ({ pathname }) => pathname });

    return (
        <header className="flex justify-between items-start xl:items-center p-4 border-b mb-2 xl:mb-4">
            <Link to="/" className="relative inline-block">
                {isDefaultColor ? (
                    <img
                        src="/assets/logo.webp"
                        alt="AREDL_logo"
                        className="block w-auto max-w-37.5"
                        loading="eager"
                        fetchPriority="high"
                    />
                ) : (
                    <>
                        <ThemedImage
                            src="/assets/logo_base.webp"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                            saturationCorrection={saturation}
                            alt="AREDL_logo_base"
                            className="block w-auto max-w-37.5"
                            maxWidth={150}
                        />
                        <ThemedImage
                            src="/assets/logo_highlight.webp"
                            hueRotation={highlightHue}
                            lightnessCorrection={lightness + highlightLightness}
                            saturationCorrection={saturation}
                            alt="AREDL_logo_highlight"
                            className="absolute inset-0 w-full h-full z-10"
                            maxWidth={150}
                        />
                        <ThemedImage
                            src="/assets/logo_other.webp"
                            hueRotation={0}
                            lightnessCorrection={lightness}
                            alt="AREDL_logo_other"
                            className="absolute inset-0 w-full h-full z-20"
                            maxWidth={150}
                        />
                    </>
                )}
            </Link>

            <Navbar />

            <div className="hidden xl:flex flex-row items-center gap-2">
                <SiDiscord
                    className="w-12 h-12 p-2.5 cursor-pointer bg-primary text-primary-foreground rounded-full transition-transform hover:scale-110"
                    onClick={() =>
                        window.open('https://discord.gg/aredl', '_blank')
                    }
                />
                {auth?.username && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <div className="relative cursor-pointer transition-transform hover:scale-110">
                                <Bell
                                    fill="currentColor"
                                    className="w-12 h-12 p-2.5 bg-primary text-primary-foreground rounded-full"
                                />
                                {notificationCount > 0 && (
                                    <div className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full border-2 border-background-light min-w-5 text-center leading-none">
                                        {notificationCount > 9
                                            ? '9+'
                                            : notificationCount}
                                    </div>
                                )}
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <Suspense fallback={<Load />}>
                                <Notifications />
                            </Suspense>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}

                <div className="flex items-center">
                    {auth?.username ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <img
                                    src={avatarUrl}
                                    alt="Avatar"
                                    className="size-12 rounded-full cursor-pointer transition-transform hover:scale-110"
                                    referrerPolicy="no-referrer"
                                    crossOrigin="anonymous"
                                />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {scopes.length > 0 && (
                                    <>
                                        <div className="flex items-center justify-between px-4 py-2">
                                            <Switch
                                                id="staff-mode"
                                                checked={staff_mode}
                                                onCheckedChange={setStaffMode}
                                            />
                                            <label
                                                htmlFor="staff-mode"
                                                className="text-secondary-light"
                                            >
                                                {staff_mode
                                                    ? 'STAFF MODE'
                                                    : 'USER MODE'}
                                            </label>
                                        </div>

                                        <Separator />
                                    </>
                                )}

                                <DropdownProfileItem
                                    label="PROFILE"
                                    to={{
                                        to: `/profile/user/$userid`,
                                        params: {
                                            userid: `${auth?.username ?? ''}`,
                                        },
                                    }}
                                />
                                <DropdownProfileItem
                                    label="CLAN"
                                    to={
                                        auth?.clan
                                            ? {
                                                  to: `/profile/clan/$clanid`,
                                                  params: {
                                                      clanid: `${auth?.clan?.id ?? ''}`,
                                                  },
                                              }
                                            : {
                                                  to: '/clan',
                                              }
                                    }
                                />
                                <DropdownProfileItem
                                    label="SETTINGS"
                                    to={{
                                        to: `/settings`,
                                    }}
                                />
                                <DropdownProfileItem
                                    label="LOGOUT"
                                    to={{
                                        to: `/logout`,
                                        search: {
                                            redirectTo: pathname,
                                        },
                                    }}
                                />
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Button onClick={login}>LOGIN</Button>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
